import React from 'react';
import Head from 'next/head';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Link from 'next/link';
import CompetitorComparison from '../components/CompetitorComparison';

export default function WhyPersonalia() {
  const audienceSegments = [
    {
      id: 'web-canvas',
      title: 'Outgrown Web-Canvas Tools?',
      subtitle: 'For marketing teams who need more than "text on an image"',
      problem: 'You started with web-based image generators for quick social graphics, but now your brand guidelines demand more. Kerning, specific font weights, text-wrap around objects, complex layouts — web-canvas tools simply cannot replicate professional design standards.',
      painPoints: [
        'Fonts render differently than your brand guidelines specify',
        'No support for advanced typography (GREP styles, optical kerning)',
        'Layouts shift unpredictably when content length varies',
        'Output looks "webby" — not suitable for premium brand materials'
      ],
      solution: 'Personalia renders your InDesign templates server-side with full fidelity — not a web browser taking screenshots. Your designers work in InDesign, tag the dynamic elements, and upload. Every output matches your brand guidelines exactly.',
      cta: 'See pixel-perfect output',
      ctaLink: '#live-demo'
    },
    {
      id: 'block-editors',
      title: 'Block-Based Editors Holding You Back?',
      subtitle: 'For high-value service providers whose proposals deserve better',
      problem: 'Your £500k+ proposals look like £15 templates. Block-based web editors restrict your creative team to generic layouts that undermine your premium positioning. In luxury markets, the medium is the message.',
      painPoints: [
        'Block-based editors limit creative expression',
        'Templates look generic — indistinguishable from competitors',
        'No support for sophisticated typography or custom layouts',
        'Documents feel "automated" rather than bespoke'
      ],
      solution: 'Design proposals in InDesign with full creative control — custom typography, sophisticated layouts, premium finishes. Then automate personalisation without sacrificing the prestige your brand demands.',
      cta: 'Elevate your proposals',
      ctaLink: '/contact'
    },
    {
      id: 'html-pdf',
      title: 'Escape HTML-to-PDF Hell',
      subtitle: 'For developers tired of fighting CSS across page breaks',
      problem: 'You\'ve tried Puppeteer, wkhtmltopdf, and every HTML-to-PDF library. Hours spent fighting CSS to make documents look good across page breaks. Headers that don\'t repeat. Footers that break. Tables that split mid-row.',
      painPoints: [
        'CSS page-break rules are inconsistent across browsers',
        'Headers and footers require complex workarounds',
        'Typography control is limited to web fonts',
        'Print-ready output (CMYK, bleeds) is nearly impossible'
      ],
      solution: 'Let designers handle design in InDesign — where page breaks, headers, and typography work as expected. You make API calls, receive production-ready documents. No CSS battles. No browser inconsistencies.',
      cta: 'See the API in action',
      ctaLink: '/for-infrastructure-engineers'
    }
  ];

  const valueProps = [
    {
      stat: '70%',
      label: 'Reduction in production time',
      description: 'Eliminate the rebuild cycle — design once in InDesign, produce at scale'
    },
    {
      stat: '100%',
      label: 'Brand fidelity',
      description: 'Pixel-perfect output that matches your InDesign designs exactly'
    },
    {
      stat: '0',
      label: 'Servers to manage',
      description: 'Cloud-native SaaS — we handle infrastructure, you focus on output'
    }
  ];

  return (
    <>
      <Head>
        <title>Why Personalia | Stop Settling for "Close Enough" Design Automation</title>
        <meta name="description" content="Outgrown web-based design automation? Frustrated with block-based template editors? Tired of HTML-to-PDF battles? Discover why scaling teams choose Personalia for professional-grade automated design." />
      </Head>

      <Header />

      <main id="main-content">
        {/* Hero Section */}
        <section className="bg-gray-900 text-white py-16 md:py-24">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-primary bg-opacity-20 text-primary-light rounded-full px-4 py-2 text-sm font-medium mb-6">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                For teams who've outgrown basic automation
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Transform InDesign Into a High Performance Production Engine.
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Most automated-personalization tools force designers to use clunky web editors. Personalia offers Industrial‑grade automation for personalized PDFs and images, powered by your existing InDesign workflow.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link href="#comparison" className="btn btn-primary btn-lg">
                  See the Difference
                </Link>
                <Link href="/contact" className="btn btn-outline-light btn-lg">
                  Talk to Us
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="py-12 bg-white border-b border-gray-100">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {valueProps.map((prop, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl md:text-5xl font-bold text-primary mb-2">{prop.stat}</div>
                  <div className="text-lg font-semibold text-gray-900 mb-1">{prop.label}</div>
                  <p className="text-sm text-gray-600">{prop.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Audience Segments */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto mb-16 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Sound Familiar?
              </h2>
              <p className="text-xl text-gray-600">
                You've validated the need for automation. Now you're being embarrassed by the output.
              </p>
            </div>

            <div className="space-y-16">
              {audienceSegments.map((segment, index) => (
                <div 
                  key={segment.id} 
                  id={segment.id}
                  className={`flex flex-col ${index % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} gap-12 items-center`}
                >
                  <div className="md:w-1/2">
                    <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                      <div className="text-sm font-semibold text-red-500 uppercase tracking-wide mb-2">The Problem</div>
                      <h3 className="text-2xl md:text-3xl font-bold mb-2">{segment.title}</h3>
                      <p className="text-gray-500 mb-4">{segment.subtitle}</p>
                      <p className="text-gray-700 mb-6">{segment.problem}</p>
                      <ul className="space-y-3">
                        {segment.painPoints.map((point, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <svg className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span className="text-gray-600 text-sm">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="md:w-1/2">
                    <div className="bg-primary rounded-2xl p-8 text-white">
                      <div className="text-sm font-semibold text-primary-light uppercase tracking-wide mb-2">The Personalia Solution</div>
                      <p className="text-lg mb-6 opacity-90">{segment.solution}</p>
                      <Link href={segment.ctaLink} className="inline-flex items-center gap-2 bg-white text-primary font-semibold px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                        {segment.cta}
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Detailed Comparison */}
        <section className="py-16 md:py-24 bg-white" id="comparison">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto mb-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                The Technical Difference
              </h2>
              <p className="text-xl text-gray-600">
                Why professional teams choose Personalia over web-based automation tools.
              </p>
            </div>

            <div className="max-w-5xl mx-auto">
              <CompetitorComparison variant="full" />
            </div>
          </div>
        </section>

        {/* ROI Section */}
        <section className="py-16 md:py-24 bg-gray-900 text-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  The Business Case
                </h2>
                <p className="text-xl text-gray-300">
                  Addressing the investment question head-on.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white bg-opacity-5 rounded-xl p-8 border border-white border-opacity-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-primary bg-opacity-20 p-2 rounded-lg">
                      <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold">Labour Savings</h3>
                  </div>
                  <p className="text-gray-300">
                    How many hours does your design team spend re-creating Adobe assets into web-based template builders? 
                    If they save <span className="text-white font-semibold">15 hours a month</span>, Personalia has already paid for itself.
                  </p>
                </div>

                <div className="bg-white bg-opacity-5 rounded-xl p-8 border border-white border-opacity-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-primary bg-opacity-20 p-2 rounded-lg">
                      <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold">Brand Integrity</h3>
                  </div>
                  <p className="text-gray-300">
                    In luxury markets, <span className="text-white font-semibold">the medium is the message</span>. 
                    If your document looks automated, your brand feels cheap. 
                    Personalia is the only way to automate without looking like you automated.
                  </p>
                </div>
              </div>

              <div className="mt-12 text-center">
                <Link href="/pricing" className="btn btn-primary btn-lg">
                  View Pricing
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Live Demo Section */}
        <section className="py-16 md:py-24 bg-gray-50" id="live-demo">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                See It In Action
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Try our live demo and experience the difference professional-grade automation makes.
              </p>
              <Link href="/#live-demo" className="btn btn-primary btn-lg">
                Try the Live Demo
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Stop Settling?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Join the teams who've graduated from "good enough" to pixel-perfect automation.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link href="/contact" className="btn btn-primary btn-lg">
                  Get Started
                </Link>
                <Link href="/for-creative-authorities" className="btn btn-outline-dark btn-lg">
                  For Creative Authorities
                </Link>
                <Link href="/for-infrastructure-engineers" className="btn btn-outline-dark btn-lg">
                  For Infrastructure Engineers
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
